/**
 * Error reporting for the operator panel — the strictest of the four surfaces, because this one
 * displays player data in cleartext by design. A breadcrumb recording a click can carry the URL of
 * the player being reviewed; a captured message can quote the field an operator just corrected.
 *
 * Inert without `VITE_SENTRY_DSN`: the SDK ships in the bundle either way and does nothing without
 * configuration, which is the state required until the data-processing agreement is confirmed.
 */

import * as Sentry from "@sentry/react";
import { defineRedactionPolicy, redactSecretsInText } from "@utopia-well-done-ltd/contracts";

/**
 * The shared core plus this panel's own realities: the operator session, and the player identifiers
 * that appear in this app's URLs and API paths.
 */
const POLICY = defineRedactionPolicy({ add: ["operatorSessionId", "playerId", "userId"] });

/** Free text leaves this app only through here. */
function scrubText(text: string): string {
  return redactSecretsInText(POLICY, text);
}

/**
 * Player identifiers ride in paths (`/players/<uuid>`), which value-level scrubbing cannot see —
 * a UUID is not secret-shaped. Every URL is therefore reduced to its route shape before it leaves.
 */
function scrubUrl(url: string): string {
  return scrubText(url).replaceAll(
    /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
    ":id",
  );
}

/**
 * Exported for tests: hooks are pure functions over event shapes, so the guarantees are assertable
 * without a DSN or a network. Review found the previous hooks scrubbed around holes — console
 * breadcrumb `data.arguments`, navigation `data.from`/`data.to`, `request.headers.Referer`, and
 * `extra.__serialized__` from a rejected non-Error — all shipped by the DEFAULT integrations, which
 * `integrations: []` does NOT disable: a user array is merged with the defaults, not substituted.
 */
export function scrubBreadcrumb<
  T extends {
    message?: string;
    data?: Record<string, unknown> | undefined;
  },
>(breadcrumb: T): T {
  if (breadcrumb.message) breadcrumb.message = scrubText(breadcrumb.message);
  if (breadcrumb.data) {
    const data: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(breadcrumb.data)) {
      // URLs (fetch/xhr/navigation from/to) reduced to route shape. Finite numbers pass: an HTTP
      // status_code or a body size cannot carry player data, and dropping them cost real triage —
      // requests showed up in events with no status. Everything else is dropped — console
      // arguments in particular can carry whole player objects.
      data[key] =
        typeof value === "string"
          ? scrubUrl(value)
          : typeof value === "number" && Number.isFinite(value)
            ? value
            : "[dropped]";
    }
    breadcrumb.data = data;
  }
  return breadcrumb;
}

export function scrubEvent<
  T extends {
    message?: string;
    request?: { url?: string; headers?: Record<string, string> } | undefined;
    extra?: Record<string, unknown> | undefined;
    exception?: { values?: { value?: string }[] } | undefined;
    breadcrumbs?: { message?: string; data?: Record<string, unknown> | undefined }[] | undefined;
  },
>(event: T): T {
  if (event.message) event.message = scrubText(event.message);
  if (event.request) {
    if (event.request.url) event.request.url = scrubUrl(event.request.url);
    // The Referer is the PREVIOUS full URL — the player page the operator came from.
    event.request.headers = undefined as never;
  }
  // A rejected non-Error is serialised whole into extra by the SDK. Nothing in it is worth a leak.
  event.extra = undefined as never;
  for (const exception of event.exception?.values ?? []) {
    if (exception.value) exception.value = scrubText(exception.value);
  }
  for (const breadcrumb of event.breadcrumbs ?? []) scrubBreadcrumb(breadcrumb);
  return event;
}

export function registerErrorReporting(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE as string | undefined,
    // Errors only. No tracing, and NO session replay on this surface, ever: a replay of an operator
    // panel is a recording of player data in cleartext, and no masking setting makes that a
    // reasonable thing to ship to a third party.
    tracesSampleRate: 0,
    /**
     * `integrations: []` merges with the defaults instead of replacing them — verified in the SDK.
     * `false` is the only real off-switch; the allowlist below is what this panel actually needs,
     * with console breadcrumbs off entirely: an operator's console.error carries player objects.
     */
    defaultIntegrations: false,
    integrations: [
      Sentry.inboundFiltersIntegration(),
      Sentry.functionToStringIntegration(),
      Sentry.dedupeIntegration(),
      Sentry.globalHandlersIntegration(),
      Sentry.linkedErrorsIntegration(),
      Sentry.breadcrumbsIntegration({ console: false }),
    ],
    sendClientReports: false,
    beforeBreadcrumb: (breadcrumb) => scrubBreadcrumb(breadcrumb),
    beforeSend: (event) => scrubEvent(event),
  });
}
